import Button from 'components/base/Button';
import SocialProfileCard from 'components/cards/SocialProfileCard';
import SettingsChangePassword from 'components/forms/SettingsChangePassword';
import SettingsPersonalInfo from 'components/forms/SettingsPersonalInfo';
import { Col, Row, Form } from 'react-bootstrap';
import profileImage from 'assets/img/team/20.webp';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { GET_EMPLOYEE, UPDATE_EMPLOYEE } from 'services/api/employee';
import { useNavigate, useParams } from 'react-router-dom';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { Employee } from 'data/users';
import { ProfilePlaceholder } from 'components/common/ProfilePlaceHolder';
import { useAccessToken } from 'providers/AccessTokenProvider';
import {
  CreateEmployeeDTO,
  CreateEmployeeFormData
} from 'data/from-data/create-employee-form.data';
import { toast } from 'react-hot-toast';
import { formatPhoneNumber } from './utils/helper';

const UpdateEmployee = () => {
  const [validated, setValidated] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isFieldOperator, setIsFieldOperator] = useState<boolean>(false);
  const [isCallOutUser, setIsCallOutUser] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [employeeDetails, setEmployeeDetails] = useState<Employee | null>(null);
  const [editEmployeeDto, setEditEmployeeDto] =
    useState<CreateEmployeeFormData>(new CreateEmployeeFormData());
  const { authUser } = useAccessToken();
  const { id } = useParams<{ id?: string }>();
  const {
    data: employee,
    isFetching,
    isSuccess
  } = useQuery(
    ['getEmployeeDetails', id],
    () => (id ? GET_EMPLOYEE(id) : null),
    {
      staleTime: 3 * 60 * 60 * 1000
    }
  );

  const queries = useQueryClient();
  const navigate = useNavigate();
  const editEmployeeAction = useMutation('editEmployee', UPDATE_EMPLOYEE, {
    onError: (err: any) => {
      toast.error(err.response.data.messages || 'Error updating employee');
      setLoading(false);
    },
    onSuccess: () => {
      setLoading(false);
      toast.success('Employee updated successfully');
      queries.invalidateQueries('getEmployeeDetails');
      queries.invalidateQueries('get_employee');
      navigate('/home/apps/employee');
    },
    onMutate: () => {
      setLoading(true);
    }
  });

  useEffect(() => {
    if (isSuccess && employee) {
      setEmployeeDetails(employee?.data);
      setIsAdmin(employee?.data?.isAdmin || false);
      setIsFieldOperator(employee?.data?.isFieldOperator || false);
      setIsCallOutUser(employee?.data?.isCallOutUser || false);
      const { data: employeeData }: any = employee;
      setEditEmployeeDto(prevData => {
        const updatedData = Object.keys(prevData).reduce((acc: any, key) => {
          acc[key] = {
            ...prevData[key],
            value: employeeData[key] || prevData[key].value
          };
          return acc;
        }, {});
        return updatedData;
      });
    }
  }, [employee, isSuccess]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === 'roles') {
      setEditEmployeeDto(prevData => ({
        ...prevData,
        [name]: {
          ...prevData[name],
          value: [{ _id: value }]
        }
      }));
    } else {
      setEditEmployeeDto(prevData => ({
        ...prevData,
        [name]: {
          ...prevData[name],
          value
        }
      }));
    }
  };

  const handleAdminStatusChange = (isAdmin: boolean) => {
    setIsAdmin(isAdmin);

    // If setting admin to true, ensure field operator is false
    if (isAdmin) {
      setIsFieldOperator(false);
    }

    setEditEmployeeDto(prevData => ({
      ...prevData,
      isAdmin: {
        ...prevData.isAdmin,
        value: isAdmin
      },
      isFieldOperator: {
        ...prevData.isFieldOperator,
        value: isAdmin ? false : prevData.isFieldOperator?.value || false
      },
      roles: {
        ...prevData.roles,
        value: isAdmin ? [] : prevData.roles.value
      }
    }));
  };

  const handleFieldOperatorStatusChange = (isFieldOperator: boolean) => {
    setIsFieldOperator(isFieldOperator);

    // If setting field operator to true, ensure admin is false
    if (isFieldOperator) {
      setIsAdmin(false);
    }

    setEditEmployeeDto(prevData => ({
      ...prevData,
      isFieldOperator: {
        ...prevData.isFieldOperator,
        value: isFieldOperator
      },
      isAdmin: {
        ...prevData.isAdmin,
        value: isFieldOperator ? false : prevData.isAdmin?.value || false
      }
    }));
  };

  const handleCallOutUserStatusChange = (isCallOut: boolean) => {
    setIsCallOutUser(isCallOut);
    setEditEmployeeDto(prevData => ({
      ...prevData,
      isCallOutUser: {
        ...prevData.isCallOutUser,
        value: isCallOut
      }
    }));
  };

  const handleSubmit = (event: FormEvent) => {
    const form = event.currentTarget as HTMLFormElement;

    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    } else {
      return runFormSubmissionAction(event);
    }

    setValidated(true);
  };

  const runFormSubmissionAction = async (e: FormEvent) => {
    e.preventDefault();

    const {
      email: { value: email },
      firstName: { value: firstName },
      lastName: { value: lastName },
      username: { value: username },
      password: { value: password },
      phoneNumber: { value: phoneNumber },
      roles: { value: roles },
      isAdmin: { value: isAdmin },
      isFieldOperator: { value: isFieldOperator },
      isCallOutUser: { value: isCallOutUser }
    } = editEmployeeDto;
    const payload: CreateEmployeeDTO = {
      ...editEmployeeDto,
      email,
      firstName,
      lastName,
      username,
      password,
      phoneNumber: formatPhoneNumber(phoneNumber),
      roles: isAdmin
        ? []
        : roles.map((role: any) => (role as any).id || role._id),
      verified: true,
      isAdmin: isAdmin || false,
      isFieldOperator: isFieldOperator || false,
      isCallOutUser: isCallOutUser || false
    };
    await editEmployeeAction.mutateAsync({ employeeId: id, formData: payload });
  };
  return (
    <>
      {isFetching ? (
        <ProfilePlaceholder />
      ) : (
        <div className="mb-9">
          <Row className="g-6">
            <Col xs={12} xl={4}>
              <SocialProfileCard
                avatar={profileImage}
                employee={employeeDetails as Employee}
              />
            </Col>
            <Col xs={12} xl={8}>
              <div className="border-bottom border-300 mb-4">
                <Form noValidate validated={validated} onSubmit={handleSubmit}>
                  <SettingsPersonalInfo
                    formData={editEmployeeDto}
                    onDataChange={handleInputChange}
                    isAdmin={isAdmin}
                    onAdminStatusChange={handleAdminStatusChange}
                    currentUserIsAdmin={authUser?.isAdmin}
                    isFieldOperator={isFieldOperator}
                    onFieldOperatorStatusChange={
                      handleFieldOperatorStatusChange
                    }
                    isCallOutUser={isCallOutUser}
                    onCallOutUserStatusChange={handleCallOutUserStatusChange}
                  />
                  <Row className="gx-3 mb-6 gy-6 gy-sm-3">
                    <Col xs={12} sm={12}>
                      <SettingsChangePassword
                        authUser={authUser as Employee}
                        formData={editEmployeeDto}
                        handleInputChange={handleInputChange}
                      />
                    </Col>
                  </Row>
                  <div className="text-end mb-6">
                    <Button
                      onClick={() => navigate('/home')}
                      variant="phoenix-secondary"
                      className="me-2"
                    >
                      Cancel Changes
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      disabled={isLoading}
                      variant="phoenix-primary"
                    >
                      Save Information
                    </Button>
                  </div>
                </Form>
              </div>
              <Row className="gy-5">
                <Col xs={12} md={6}>
                  <h4 className="text-black">Account Deletion</h4>
                  <p className="text-700">
                    Transfer this account to another person or to a company
                    repository.
                  </p>
                  <Button variant="phoenix-danger">Delete account</Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      )}
    </>
  );
};

export default UpdateEmployee;
